import { useEffect, useLayoutEffect, useState, useTransition, type ReactNode } from 'react';
import { PlaceContext, currentPlace, subscribe, takeScroll, type Place } from './router.ts';

/**
 * Round the app (app.tsx). A move the app `holds` (staff page to staff page)
 * is a React transition: the page moved from stays on screen, still reading
 * its own place, until the next one has its file; `useMoving` says one is on
 * its way. Any other move shows at once, as a page that still runs while
 * held could act on what it just did (the join page, seeing its new member
 * pass, would move on again). It reads the place again once it has
 * subscribed: a child that navigates in its first effect (a redirect) runs
 * before it subscribes.
 */
export function PlaceProvider({ holds, children }: { readonly holds: (from: Place, to: Place) => boolean; readonly children: ReactNode }) {
  const [place, setPlace] = useState(currentPlace);
  const [moving, startTransition] = useTransition();
  useEffect(() => {
    let shown = place;
    const update = () => {
      const next = currentPlace();
      const from = shown;
      shown = next;
      if (holds(from, next))
        startTransition(() => {
          setPlace(next);
        });
      else setPlace(next);
    };
    const stop = subscribe(update);
    update();
    return stop;
  }, []);
  useLayoutEffect(() => {
    takeScroll();
  }, [place]);
  return <PlaceContext.Provider value={{ place, moving }}>{children}</PlaceContext.Provider>;
}
